import { z } from 'zod';
import { isoUtcDateTimeSchema } from '../date-time-schema';
import { dependencyTypeSchema } from './dependency-dto';
import { schedulingTypeSchema } from './item-schedule-dto';

export const dependencySchema = z.object({
  dependencyType: dependencyTypeSchema,
  dependencyItemId: z.number().int().positive(),
});

export const estimationSchemaSchema = z.object({
  id: z.number().int().positive(),
  label: z.string(),
  highEffort: z.number().int().nullable(),
  lowEffort: z.number().int().nullable(),
  priority: z.string(),
  estimationSchemeId: z.number().int().positive(),
});

export const customFieldValueEntrySchema = z.object({
  value: z.union([z.string(), z.number(), z.boolean()]).nullable(),
  description: z.string().optional(),
});

export const customFieldValuesEntrySchema = z.union([
  customFieldValueEntrySchema,
  z.array(customFieldValueEntrySchema),
  z.null(),
]);

export const customFieldValuesSchema = z.record(z.string(), customFieldValuesEntrySchema);

export const schedulesSchema = z.object({
  forecastedStartDateTime: isoUtcDateTimeSchema.optional(),
  forecastedFinishDateTime: isoUtcDateTimeSchema.optional(),
  latestFinishDateTime: isoUtcDateTimeSchema.optional(),
});

export const itemSchema = z.object({
  id: z.number().int().positive(),
  loopId: z.number().int().positive().optional(),
  itemTypeId: z.number().int().positive().optional(),
  itemStatusId: z.number().int().positive().optional(),
  name: z.string().optional(),
  priority: z.string().optional(),
  schedulingType: schedulingTypeSchema.optional(),
  createdAt: isoUtcDateTimeSchema.optional(),
  updatedAt: isoUtcDateTimeSchema.optional(),
  createdBy: z.string().optional(),
  updatedBy: z.string().optional(),
  description: z.string().nullable().optional(),
  parentId: z.number().int().positive().nullable().optional(),
  teamId: z.number().int().positive().nullable().optional(),
  estimationId: z.number().int().positive().nullable().optional(),
  timeSpentSeconds: z.number().int().nullable().optional(),
  earliestStartDateTime: isoUtcDateTimeSchema.nullable().optional(),
  deadlineDateTime: isoUtcDateTimeSchema.nullable().optional(),
  plannedStartDateTime: isoUtcDateTimeSchema.nullable().optional(),
  plannedFinishDateTime: isoUtcDateTimeSchema.nullable().optional(),
  doneDateTime: isoUtcDateTimeSchema.nullable().optional(),
  externalKey: z.string().nullable().optional(),
  externalLink: z.string().nullable().optional(),
  externalCreatedAt: isoUtcDateTimeSchema.nullable().optional(),
  dependencies: z.array(dependencySchema).optional(),
  schedules: schedulesSchema.optional(),
  estimationSchema: estimationSchemaSchema.nullable().optional(),
  customFieldValues: customFieldValuesSchema.optional(),
});

export const itemBulkFetchResponseSchema = z.object({
  itemsById: z.record(z.string(), itemSchema),
});

export const itemBulkFetchFieldIdSchema = z.enum([
  'id',
  'name',
  'description',
  'itemTypeId',
  'itemStatusId',
  'parentId',
  'schedulingType',
  'teamId',
  'earliestStartDateTime',
  'deadlineDateTime',
  'plannedStartDateTime',
  'plannedFinishDateTime',
  'doneDateTime',
  'timeSpentSeconds',
  'priority',
  'externalKey',
  'externalLink',
  'externalCreatedAt',
  'loopId',
  'estimationId',
  'createdAt',
  'updatedAt',
  'createdBy',
  'updatedBy',
  'dependencies',
  'customFields',
  'schedules',
  'estimationSchema',
]);

export type DependencyDTO = z.infer<typeof dependencySchema>;
export type EstimationSchemaDTO = z.infer<typeof estimationSchemaSchema>;
export type SchedulesDTO = z.infer<typeof schedulesSchema>;
export type CustomFieldValueEntryDTO = z.infer<typeof customFieldValueEntrySchema>;
export type CustomFieldValuesEntryDTO = z.infer<typeof customFieldValuesEntrySchema>;
export type CustomFieldValuesDTO = z.infer<typeof customFieldValuesSchema>;
export type ItemDTO = z.infer<typeof itemSchema>;
export type ItemBulkFetchResponseDTO = z.infer<typeof itemBulkFetchResponseSchema>;
export type ItemBulkFetchFieldIdDTO = z.infer<typeof itemBulkFetchFieldIdSchema>;
